import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, of, Subscription, timer } from "rxjs";
import { User } from "../shared/services/user.service";
import { AppSettingsService } from "../shared/services/app-settings.service";
import { AuthMocksService } from "../shared/services/mocks/auth-mocks.service";
import { PortalApiService } from "../shared/services/api/portal-api.service";
import { UserService } from "../shared/services/user.service";
import { PurchaseStateService } from "./../purchase/shared/services/purchase-state.service";
import { ViewStateService } from "../shared/services/view-state.service";
import { CookieService } from "ngx-cookie-service";
import { finalize, map, switchMap } from "rxjs/operators";
import { NavbarRefreshService } from "./../nav/shared/navbar-refresh.service";

const apiEndpoints = {
  signIn: "practitionerAccount/signIn",
  forgotPassword: "practitionerAccount/forgotPassword",
  forgotLogin: "practitionerAccount/forgotLogin",
  resetPassword: "practitionerAccount/resetPassword",
  changePassword: "practitionerAccount/{personId}/changePassword",
  createAccount: "/practitionerAccount/create",
  createProfile: "/practitionerAccount/createProfile",
  refresh: "practitionerAccount/refresh",
};

let customCookieName: string;

const CookieParams = {
  aarcId: "aarc_id",
  examId: "exam_id",
  name: "practitioner_token",
  path: "/",
  expirationDate: new Date(Date.now() + 2 * 60 * 60 * 1000)
};

@Injectable()
export class AuthService implements OnDestroy {
  private cookieParams = CookieParams;
  private tokenExpirationCheck$!: Subscription;
  private tokenCheckInterval = 300000; // checks every 5 mins

  constructor(
    private cookieService: CookieService,
    private appSettings: AppSettingsService,
    private portalApiService: PortalApiService,
    private router: Router,
    private userService: UserService,
    private purchaseState: PurchaseStateService,
    private viewState: ViewStateService,
    private authMocksService: AuthMocksService,
    private navBarRefreshService: NavbarRefreshService
  ) {
    // Initialize the token check
    this.initializeTokenCheck();
  }

  private initializeTokenCheck(): void {
    const tokenImpersonate = this.router.routerState.snapshot.root.queryParams['token'];
    if (!tokenImpersonate) {
      const token = this.getTokenFromCookie();
      if (token) {
        const user = this.getUserFromToken(token);
        if (user) {
          this.userService.setActiveUser(user);
          this.startTokenExpirationChecker();
        } else {
          this.signOut();
        }
      }
    }
  }

  private startTokenExpirationChecker(): void {
    if (this.tokenExpirationCheck$) {
      this.tokenExpirationCheck$.unsubscribe();
    }

    this.tokenExpirationCheck$ = timer(0, this.tokenCheckInterval)
      .pipe(switchMap(() => this.validateTokenAndHandleExpiration()))
      .subscribe();
  }

  stopTokenExpirationChecker(): void {
    if (this.tokenExpirationCheck$) {
      this.tokenExpirationCheck$.unsubscribe();
      this.tokenExpirationCheck$ = null;
    }
  }

  private validateTokenAndHandleExpiration(): Observable<void> {
    const token = this.getTokenFromCookie();
    if (token) {
      const user = this.getUserFromToken(token);
      const isTokenExpired = this.isTokenExpired(token);
      if (!user || isTokenExpired) {
        console.log("Token is expired or user not found. Logging out...");
        this.signOut();
      }
    } else {
      console.log("No token found. Logging out...");
      this.signOut();
    }
    return of();
  }

  private isTokenExpired(token: string): boolean {
    const expiryTime = this.getTokenExpiryTime(token);
    return expiryTime ? new Date().getTime() > expiryTime : true;
  }

  // private getTokenExpiryTime(token: string): number | null {
  //   try {
  //     const payloadBase64 = token.split(".")[1];
  //     const decodedPayload = JSON.parse(atob(payloadBase64));
  //     const exp = decodedPayload.exp;
  //     if (exp) {
  //       return exp * 1000;
  //     }
  //     return null;
  //   } catch (error) {
  //     console.error("Error decoding token:", error);
  //     return null;
  //   }
  // }
  private getTokenExpiryTime(token: string): number | null {
    try {

      let payloadBase64 = token.split('.')[1];

      // Convert Base64URL -> Base64
      payloadBase64 = payloadBase64
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      // Restore padding
      while (payloadBase64.length % 4) {
        payloadBase64 += '=';
      }

      const decodedPayload = JSON.parse(atob(payloadBase64));

      const exp = decodedPayload.exp;

      if (exp) {
        return exp * 1000;
      }

      return null;

    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  setAuthToken(tokenIn: string) {
    if (!tokenIn) {
      this.router.navigate(["auth"]);
    } else {
      try {
        const token: string = tokenIn;
        const user = this.getUserFromToken(token);
        customCookieName = `${this.cookieParams.name}.${user.firstName}`;
        localStorage.setItem("customCookieName", customCookieName);

        this.cookieService.set(
          customCookieName,
          token,
          new Date(this.getTokenExpiryTime(token)),
          this.cookieParams.path
        );
        this.userService.setActiveUser(user);
        this.router.navigate([""]);
      } catch (e) {
        if (this.appSettings.IsDebugEnabled) {console.log(e);}
        this.router.navigate(["auth"]);
      }
    }
  }

  refreshToken() {
    return this.portalApiService
      .get(apiEndpoints.refresh, "")
      .pipe(
        map((resp: Response) => this.handleRefreshResponse(resp)),
        finalize(() => {
          return false;
        })
      );
  }

  signIn(email: string, password: string, aarcId: string, examId: string): Observable<boolean> {
    if (this.checkMockUserEnabled(email, password)) {
      return this.createMockUserResponse();
    }
  
    const user = { email, password, aarcId, examId };
  
    return this.portalApiService
      .post(apiEndpoints.signIn, user)
      .pipe(
        map((resp: Response) => this.handleLoginResponse(resp)),
        finalize(() => {
          return false;
        })
      );
  }

  createProfile(person: any): Observable<any> {
    return this.portalApiService
      .post(apiEndpoints.createProfile, person)
      .pipe(
        map((resp) => {
          if (resp.status === 200) {
            return resp;
          }
          return resp;
        }),
        finalize(() => {
          return null;
        })
      );
  }

  signOut() {
    this.removeCookies();

    this.userService.clearActiveUser();
    this.stopTokenExpirationChecker();
    this.purchaseState.reset();
    this.router.navigate(["auth"]);

    this.viewState.stopLoading();
  }

  private removeCookies() {
    const localstorageCookieName = localStorage.getItem("customCookieName");
    this.cookieService.delete(localstorageCookieName, this.cookieParams.path);
    localStorage.removeItem("customCookieName");

    this.cookieService.delete(this.cookieParams.aarcId, this.cookieParams.path);
    this.cookieService.delete(this.cookieParams.examId, this.cookieParams.path);
  }

  register(email: string, zip: string, ssn: string, recaptcha: string) {
    return this.portalApiService
      .post(apiEndpoints.createAccount, {
        email,
        recaptcha,
        zip,
        ssn,
        confirmSsn: ssn,
      })
      .pipe(
        finalize(() => {
          return false;
        })
      );
  }

  forgotPassword(email: string) {
    return this.portalApiService
      .post(apiEndpoints.forgotPassword, { email })
      .pipe(
        finalize(() => {
          return false;
        })
      );
  }
  
  forgotLogin(record: any) {
    return this.portalApiService
      .post(apiEndpoints.forgotLogin, record)
      .pipe(
        finalize(() => {
          return null;
        })
      );
  }
  
  resetPassword(email: string, token: string, password: string) {
    return this.portalApiService
      .post(apiEndpoints.resetPassword, {
        email,
        password,
        confirmPassword: password,
        token,
      })
      .pipe(
        finalize(() => {
          return false;
        })
      );
  }
  
  changePassword(
    personId: number,
    email: string,
    oldpassword: string,
    newpassword: string,
    confirmPassword: string
  ) {
    const url = apiEndpoints.changePassword.replace("{personId}", personId.toString());
  
    return this.portalApiService
      .post(url, {
        email,
        originalPassword: oldpassword,
        password: newpassword,
        confirmPassword,
      })
      .pipe(
        finalize(() => {
          return false;
        })
      );
  }

  private checkMockUserEnabled(email: string, password: string) {
    return (
      this.appSettings.IsDebugEnabled &&
      !this.appSettings.IsProductionMode &&
      email === this.authMocksService.email &&
      password === this.authMocksService.password
    );
  }

  private createMockUserResponse(): Observable<boolean> {
    console.log("mock user enabled");
    return new BehaviorSubject<boolean>(
      this.handleLoginResponse(this.authMocksService.createSignInResponse())
    );
  }

  private handleLoginResponse(response: any): boolean {
    if (response && this.setToken(response)) {
      this.startTokenExpirationChecker();
      this.router.navigate([""]);
      return true;
    }
    return false;
  }

  private handleRefreshResponse(response: any): boolean {
    if (response && response.token && this.setToken(response)) {
      this.startTokenExpirationChecker();
      this.navBarRefreshService.triggerRefresh(); 
      return true;
    } else {
      this.signOut();
      return false;
    }
  }

  private setToken(response: any): boolean {
    if (!response || typeof response.token !== "string") {
      return false;
    }

    try {
      const token: string = response.token;
      const user = this.getUserFromToken(token);

      if (!user) {
        return false;
      }

      const customCookieName = `${this.cookieParams.name}.${user.firstName}`;
      localStorage.setItem("customCookieName", customCookieName);

      this.cookieService.set(
        customCookieName,
        token,
        new Date(this.getTokenExpiryTime(token)),
        this.cookieParams.path
      );

      this.userService.setActiveUser(user);
      return true;
    } catch (e) {
      if (this.appSettings.IsDebugEnabled) {
        console.error("Error setting token:", e);
      }
      return false;
    }
  }

  private getTokenFromCookie() {
    const localstorageCookieName = localStorage.getItem("customCookieName");
    return this.cookieService.get(localstorageCookieName);
  }

  // Decode a Base64 string to UTF-8
  private decodeBase64(base64: string): string {
  try {

    // Convert Base64URL -> Base64
      base64 = base64.replace(/-/g, '+').replace(/_/g, '/');

      // Add padding if needed
      while (base64.length % 4) {
        base64 += '=';
      }

      const binaryString = atob(base64);

      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      return new TextDecoder('utf-8').decode(bytes);

    } catch (e) {
      console.error('Error decoding Base64:', e);
      return '';
    }
  }
  // private decodeBase64(base64: string): string {
  //   try {
  //     const binaryString = atob(base64);
  //     const bytes = new Uint8Array(binaryString.length);
  //     for (let i = 0; i < binaryString.length; i++) {
  //       bytes[i] = binaryString.charCodeAt(i);
  //     }
  //     return new TextDecoder("utf-8").decode(bytes);
  //   } catch (e) {
  //     console.error("Error decoding Base64:", e);
  //     return "";
  //   }
  // }

  private getUserFromToken(token: string): User | null {
    try {
      const [header, payload] = token.split(".");
      if (!payload) {
        return null;
      }
      const decodedPayload = this.decodeBase64(payload);
      const claims = JSON.parse(decodedPayload);

      // Check if token is expired
      const currentTime = Date.now() / 1000;
      const expTime = claims.exp;

      if (expTime >= currentTime) {
        return JSON.parse(claims.portalUser);
      } else {
        return null;
      }
    } catch (e) {
      console.error("Error parsing token:", e);
      return null;
    }
  }

  ngOnDestroy(): void {
    this.stopTokenExpirationChecker();
  }
}
